/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getIndicators,
    updateIndicator,
    createIndicator,
    createIndicatorCategory,
    updateIndicatorCategory,
    getIndicatorCategories,
    deleteIndicator,
    deleteIndicatorCategory,
    getSchoolIndicators,
    getSchoolIndicatorsCalendar,
    updateSchoolIndicators,
    getSchoolIndicatorLogs,
    getSchoolIndicatorCategories,
    getCategoryIndicatorsReport,
    getIndicatorSchoolValues,
    compareSchools,
    compareSchoolsTimeSeries,
    exportSchoolsComparison,
    exportSchoolsComparisonPdf,
    exportEducationScope,
    exportEducationScopePdf,
    getEducationScopeTimeSeries,
    exportIndicators,
    generateIndicatorsImportTemplate,
    importIndicatorsValues,
    bulkUpdateIndicatorsStatus,
} from './indicatorApi';

import {
    logoutUser,
} from '../user/userApi';

import { IndicatorSchoolValuesResponse, IndicatorState, TimeSeriesApiResponse } from './types';

const initialState = {
    error: null,
    isFetching: false,
    isFetchingCategoryReport: false,
    isFetchingComparison: false,
    isFetchingTimeSeriesComparison: false,
    indicators: [],
    singleIndicator: null,
    categories: [],
    singleCategory: null,
    isIndicatorCreated: false,
    schoolIndicators: [],
    schoolCalendarIndicators: [],
    schoolIndicatorLogs: [],
    schoolIndicatorCategories: [],
    educationCenterIndicatorCategories: [],
    educationDepartmentIndicatorCategories: [],
    categoryReport: null,
    schoolComparison: null,
    timeSeriesComparison: null,
    educationScopeTimeSeries: null,
    specialIndicators: [],
    specialIndicatorsTimeSeries: [],
    isFetchingEducationScopeTimeSeries: false,
    isFetchingExport: false,
    isFetchingIndicatorsExport: false,
    isFetchingEducationScopeExport: false,
    isFetchingEducationScopePdfExport: false,
    isFetchingImportTemplate: false,
    isImportingValues: false,
    serverCompareDate: null,
    indicatorsSummary: [],
    indicatorSchoolValues: null,
    isFetchingIndicatorSchoolValues: false,
} as IndicatorState;

export const indicatorSlice = createSlice({
    name: 'indicator',
    initialState,
    reducers: {
        loadSingleIndicator: (state, action: PayloadAction<number>) => {
            const indicator = state.indicators.find(s => s.id === action.payload) || null;
            state.singleIndicator = indicator;
        },
        loadSingleIndicatorCategory: (state, action: PayloadAction<number>) => {
            const category = state.categories.find(s => s.id === action.payload) || null;
            state.singleCategory = category;
        },
        resetSingleCategory: (state) => {
            state.singleCategory = null;
        },
        resetSingleIndicator: (state) => {
            state.isIndicatorCreated = false;
            state.singleIndicator = null;
        },
    },
    extraReducers: (builder) => {
        // Update user
        builder
            .addCase(getIndicators.fulfilled, (state:IndicatorState, action) => {
                state.isFetching = false;
                state.indicators = action?.payload?.indicators;
                state.categories = action?.payload?.categories;
            });

        builder.addCase(getSchoolIndicators.fulfilled, (state:IndicatorState, action) => {
            state.isFetching = false;
            state.schoolIndicators = action?.payload?.schoolIndicators;
        });

        builder.addCase(getSchoolIndicatorsCalendar.fulfilled, (state:IndicatorState, action) => {
            state.isFetching = false;
            state.schoolCalendarIndicators = action?.payload?.indicators || [];
        });

        builder.addCase(getSchoolIndicatorLogs.fulfilled, (state:IndicatorState, action) => {
            state.isFetching = false;
            state.schoolIndicatorLogs = action?.payload?.schoolIndicatorLogs;
            console.log(action?.payload);
        });

        builder.addCase(updateSchoolIndicators.fulfilled, (state:IndicatorState, action) => {
            state.isFetching = false;
            state.schoolIndicators = action?.payload?.schoolIndicators;
            // notification.success({
            //     message: 'Pavyko!',
            //     description: 'Šios kategorijos rodikliai sėkmingai atnaujinti',
            // });
        });

        builder
            .addCase(getIndicatorCategories.fulfilled, (state:IndicatorState, action) => {
                state.isFetching = false;
                state.categories = action?.payload?.categories;
            });

        builder
            .addCase(deleteIndicator.fulfilled, (state: IndicatorState, action) => {
                state.isFetching = false;
                state.indicators = state.indicators.filter(s => s.id !== action?.payload?.id);
                if (state.singleIndicator?.id === action?.payload?.id) {
                    state.singleIndicator = null;
                }
                notification.success({
                    message: 'Pavyko!',
                    description: action?.payload?.message || 'Rodiklis sėkmingai ištrintas.',
                });
            });

        builder
            .addCase(deleteIndicatorCategory.fulfilled, (state:IndicatorState, action) => {
                state.isFetching = false;
                state.categories = state.categories.filter(s => s.id !== action?.payload?.id);
                notification.success({
                    message: 'Pavyko!',
                    description: 'Kategorija sėkmingai ištrinta',
                });
            });

        // Update survey
        builder.addCase(updateIndicator.fulfilled, (state: IndicatorState, action) => {
            state.isFetching = false;
            const updatedIndicator = action?.payload?.indicator;

            // Update singleSurvey
            state.singleIndicator = updatedIndicator;

            // Update surveys array
            const index = state.indicators.findIndex(s => s.id === updatedIndicator.id);
            if (index !== -1) {
                state.indicators[index] = updatedIndicator;
            }

            notification.success({
                message: 'Pavyko!',
                description: 'Rodiklis sėkmingai atnaujintas',
            });
        });

        builder.addCase(bulkUpdateIndicatorsStatus.pending, (state: IndicatorState) => {
            state.isFetching = true;
        });

        builder.addCase(bulkUpdateIndicatorsStatus.fulfilled, (state: IndicatorState, action) => {
            state.isFetching = false;
            const { indicator_ids, status } = action.meta.arg || {};
            const { updated_count, message } = action?.payload || {};

            // Update indicators in the array
            if (indicator_ids && Array.isArray(indicator_ids) && status !== undefined) {
                indicator_ids.forEach((id: number) => {
                    const index = state.indicators.findIndex(ind => ind.id === id);
                    if (index !== -1) {
                        const indicator = state.indicators[index];
                        if (indicator) {
                            // eslint-disable-next-line no-param-reassign
                            indicator.status = status;
                        }
                    }
                });
            }

            notification.success({
                message: 'Pavyko!',
                description: message || `Sėkmingai atnaujinta ${updated_count || 0} rodiklių`,
            });
        });

        builder.addCase(bulkUpdateIndicatorsStatus.rejected, (state: IndicatorState) => {
            state.isFetching = false;
        });

        builder.addCase(createIndicator.fulfilled, (state:IndicatorState, action) => {
            const createdIndicator = action?.payload?.indicator;

            // Update singleSurvey
            state.singleIndicator = createdIndicator;
            state.isFetching = false;
            state.isIndicatorCreated = true;
            state.indicators.push(createdIndicator);

            notification.success({
                message: 'Pavyko!',
                description: 'Rodiklis sėkmingai sukurtas',
            });
        });

        builder.addCase(createIndicatorCategory.fulfilled, (state: IndicatorState, action) => {
            const createdCategory = action?.payload?.category;
            state.categories.push(createdCategory);

            notification.success({
                message: 'Pavyko!',
                description: 'Kategorija sėkmingai sukurta',
            });

            state.isFetching = false;
        });

        builder.addCase(updateIndicatorCategory.fulfilled, (state: IndicatorState, action) => {
            const updatedCategory = action?.payload?.category;

            const categoryIndex = state.categories.findIndex(s => s.id === updatedCategory.id);
            if (categoryIndex !== -1) {
                const category = state.categories[categoryIndex];
                if (category) {
                    state.categories[categoryIndex] = updatedCategory;

                    notification.success({
                        message: 'Pavyko!',
                        description: 'Kategorija sėkmingai atnaujinta',
                    });
                }
            }
            state.isFetching = false;
        });

        builder.addCase(getSchoolIndicatorCategories.fulfilled, (state:IndicatorState, action) => {
            state.isFetching = false;
            if (action?.payload?.assigned_scope === 'education_center') {
                state.educationCenterIndicatorCategories = action?.payload?.categories;
            } else if (action?.payload?.assigned_scope === 'education_department') {
                state.educationDepartmentIndicatorCategories = action?.payload?.categories;
            } else {
                state.schoolIndicatorCategories = action?.payload?.categories;
            }
        });

        builder.addCase(getCategoryIndicatorsReport.fulfilled, (state:IndicatorState, action) => {
            state.isFetchingCategoryReport = false;
            state.categoryReport = action?.payload?.report || null;
        });

        builder.addCase(compareSchools.fulfilled, (state:IndicatorState, action) => {
            state.isFetchingComparison = false;
            state.schoolComparison = action?.payload?.comparison || null;
            state.specialIndicators = action?.payload?.special_indicators || [];
            state.serverCompareDate = action?.payload?.date || null;
            state.indicatorsSummary = action?.payload?.indicators_summary || [];
            state.timeSeriesComparison = null;
        });

        builder.addCase(compareSchoolsTimeSeries.fulfilled, (state:IndicatorState, action) => {
            state.isFetchingTimeSeriesComparison = false;
            state.timeSeriesComparison = action?.payload?.time_series || null;
            state.specialIndicatorsTimeSeries = action?.payload?.special_indicators_time_series || [];
            state.schoolComparison = null;
        });

        builder.addCase(exportSchoolsComparison.fulfilled, (state:IndicatorState) => {
            state.isFetchingExport = false;
        });

        builder.addCase(exportSchoolsComparisonPdf.fulfilled, (state:IndicatorState) => {
            state.isFetchingExport = false;
        });

        builder.addCase(exportIndicators.fulfilled, (state:IndicatorState) => {
            state.isFetchingIndicatorsExport = false;
        });

        // Indicator School Values
        builder.addCase(getIndicatorSchoolValues.pending, (state:IndicatorState) => {
            state.isFetchingIndicatorSchoolValues = true;
            state.error = null;
        });

        builder.addCase(getIndicatorSchoolValues.fulfilled, (state:IndicatorState, action) => {
            state.isFetchingIndicatorSchoolValues = false;
            const { indicator, period, period_description, statistics, schools } = action.payload as unknown as IndicatorSchoolValuesResponse;
            state.indicatorSchoolValues = {
                indicator,
                period,
                period_description,
                statistics,
                schools,
            };
        });

        builder.addCase(getIndicatorSchoolValues.rejected, (state:IndicatorState, action) => {
            state.isFetchingIndicatorSchoolValues = false;
            state.error = action.error;
        });

        // on logout
        builder
            .addCase(logoutUser.fulfilled, (state:IndicatorState) => {
                state.indicators = [];
                state.categories = [];
                state.singleIndicator = null;
                state.isIndicatorCreated = false;
                state.schoolIndicators = [];
                state.schoolCalendarIndicators = [];
                state.schoolIndicatorLogs = [];
                state.schoolComparison = null;
                state.timeSeriesComparison = null;
                state.specialIndicators = [];
                state.specialIndicatorsTimeSeries = [];
                state.indicatorSchoolValues = null;
            });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                updateIndicator.pending,
                createIndicator.pending,
                createIndicatorCategory.pending,
                updateIndicatorCategory.pending,
                getIndicatorCategories.pending,
                deleteIndicator.pending,
                deleteIndicatorCategory.pending,
                getSchoolIndicators.pending,
                getSchoolIndicatorsCalendar.pending,
                updateSchoolIndicators.pending,
                getSchoolIndicatorLogs.pending,
                getSchoolIndicatorCategories.pending,
            ),
            (state:IndicatorState) => {
                state.isFetching = true;
                state.error = null;
                state.isIndicatorCreated = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportSchoolsComparison.pending,
                exportSchoolsComparisonPdf.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingExport = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportIndicators.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingIndicatorsExport = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getCategoryIndicatorsReport.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingCategoryReport = true;
                state.error = null;
            },
        );
        builder.addMatcher(
            isAnyOf(
                compareSchools.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingComparison = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                compareSchoolsTimeSeries.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingTimeSeriesComparison = true;
                state.error = null;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getIndicators.pending,
            ),
            (state:IndicatorState) => {
                state.isFetching = true;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                updateIndicator.rejected,
                getIndicators.rejected,
                createIndicator.rejected,
                createIndicatorCategory.rejected,
                updateIndicatorCategory.rejected,
                getIndicatorCategories.rejected,
                deleteIndicator.rejected,
                deleteIndicatorCategory.rejected,
                getSchoolIndicators.rejected,
                getSchoolIndicatorsCalendar.rejected,
                updateSchoolIndicators.rejected,
                getSchoolIndicatorLogs.rejected,
                getSchoolIndicatorCategories.rejected,
                getCategoryIndicatorsReport.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportSchoolsComparison.rejected,
                exportSchoolsComparisonPdf.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetchingExport = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportIndicators.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetchingIndicatorsExport = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getCategoryIndicatorsReport.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetchingCategoryReport = false;
                state.error = action.error;
            },
        );
        builder.addMatcher(
            isAnyOf(
                compareSchools.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetchingComparison = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                compareSchoolsTimeSeries.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetchingTimeSeriesComparison = false;
                state.error = action.error;
            },
        );

        // Education Scope Time Series
        builder.addMatcher(
            isAnyOf(
                getEducationScopeTimeSeries.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopeTimeSeries = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getEducationScopeTimeSeries.fulfilled,
            ),
            (state:IndicatorState, action: PayloadAction<TimeSeriesApiResponse>) => {
                state.isFetchingEducationScopeTimeSeries = false;
                state.educationScopeTimeSeries = action.payload;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getEducationScopeTimeSeries.rejected,
            ),
            (state:IndicatorState, action) => {
                state.isFetchingEducationScopeTimeSeries = false;
                state.error = action.error;
            },
        );

        // Education Scope Export
        builder.addMatcher(
            isAnyOf(
                exportEducationScope.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopeExport = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportEducationScope.fulfilled,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopeExport = false;
                notification.success({
                    message: 'Pavyko!',
                    description: 'Duomenys sėkmingai eksportuoti',
                });
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportEducationScope.rejected,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopeExport = false;
            },
        );

        // Education Scope PDF Export
        builder.addMatcher(
            isAnyOf(
                exportEducationScopePdf.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopePdfExport = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportEducationScopePdf.fulfilled,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopePdfExport = false;
                notification.success({
                    message: 'Pavyko!',
                    description: 'PDF failas sėkmingai eksportuotas',
                });
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportEducationScopePdf.rejected,
            ),
            (state:IndicatorState) => {
                state.isFetchingEducationScopePdfExport = false;
            },
        );

        // Import Template Generation
        builder.addMatcher(
            isAnyOf(
                generateIndicatorsImportTemplate.pending,
            ),
            (state:IndicatorState) => {
                state.isFetchingImportTemplate = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                generateIndicatorsImportTemplate.fulfilled,
            ),
            (state:IndicatorState) => {
                state.isFetchingImportTemplate = false;
                notification.success({
                    message: 'Pavyko!',
                    description: 'Importo šablonas sėkmingai sugeneruotas',
                });
            },
        );

        builder.addMatcher(
            isAnyOf(
                generateIndicatorsImportTemplate.rejected,
            ),
            (state:IndicatorState) => {
                state.isFetchingImportTemplate = false;
                state.error = null;
            },
        );

        // Import indicators values
        builder.addMatcher(
            isAnyOf(
                importIndicatorsValues.pending,
            ),
            (state:IndicatorState) => {
                state.isImportingValues = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                importIndicatorsValues.fulfilled,
            ),
            (state:IndicatorState) => {
                state.isImportingValues = false;
                notification.success({
                    message: 'Pavyko!',
                    description: 'Rodiklių reikšmės sėkmingai importuotos',
                });
            },
        );

        builder.addMatcher(
            isAnyOf(
                importIndicatorsValues.rejected,
            ),
            (state:IndicatorState) => {
                state.isImportingValues = false;
            },
        );
    },
});

export const { loadSingleIndicator, resetSingleIndicator, loadSingleIndicatorCategory, resetSingleCategory } = indicatorSlice.actions;
export default indicatorSlice.reducer;
